/**
 * The knobs of one Bereich for the sysadmin panel (spec 2026-10-03-stellschrauben §9): which public
 * variables act on this role, and the snippet "Kopieren" puts on the clipboard. Read-only; the data
 * is src/editor-v2/tokens/wm.knobs.json. Loaded lazily with the panel block (knobsLint pulls postcss).
 */
import KNOBS from '@/editor-v2/tokens/wm.knobs.json';
import TOKENS from '@/editor-v2/tokens/wm.tokens.json';
import { ELEMENTS, LEVEL_ROLE } from '@/editor-v2/utils/section/elements';
import { knobsVocabulary } from '@/editor-v2/utils/knobsLint';

const vocab = knobsVocabulary(KNOBS, TOKENS);
const namesOf = (group) => [...vocab.public].filter(([, i]) => i.group === group).map(([n]) => n);

/** Every heading/text level used in the doc, as [el, stufe] (unset = the element's standard level). */
function levelsIn(doc) {
  const out = new Set();
  const walk = (node) => {
    if (Array.isArray(node)) { node.forEach(walk); return; }
    if (!node || typeof node !== 'object') return;
    if (LEVEL_ROLE[node.el]) {
      const s = node.level;
      const values = s && typeof s === 'object' ? Object.values(s) : [s || ELEMENTS[node.el].standardLevel];
      values.filter((w) => LEVEL_ROLE[node.el][w]).forEach((w) => out.add(`${node.el}:${w}`));
    }
    Object.values(node).forEach(walk);
  };
  walk(doc);
  return [...out].map((x) => x.split(':'));
}

/**
 * Rows `{ abschnitt, name, kind, wann, text, beispiel }` for the doc's Bereich, grouped by `abschnitt`
 * (tokens, stufen, haken, knoepfe). Tokens and hooks always; a knob for an element type only when the
 * Bereich has that type (or the group `x-text`), like the frozen lists of wmKnobs.spec.ts.
 */
export function knobsRows(doc, language) {
  const t = (x) => (language === 'en' ? x.en : x.de);
  const json = JSON.stringify(doc);
  const has = (el) => json.includes(`"el":"${el}"`);
  const rows = [];
  KNOBS.tokens.forEach((tok) => {
    if (tok.pattern) {
      levelsIn(doc).forEach(([el, level]) => {
        const name = `--wm-type-${LEVEL_ROLE[el][level]}-size`;
        if (rows.some((z) => z.name === name)) return;
        rows.push({
          segment: 'levels', name, kind: 'meaning', when: `data-level="${level}"`, text: t(tok.note), example: tok.example, el, level,
        });
      });
      return;
    }
    const names = tok.name ? [tok.name] : namesOf(tok.ref);
    rows.push({
      segment: 'tokens',
      name: tok.example_name || names[0],
      kind: tok.kind,
      when: tok.applies_when,
      text: t(tok.note),
      example: tok.example,
      all: names.length > 1 ? names : null,
    });
  });
  KNOBS.hooks.filter((h) => !h.element || h.element.some(has)).forEach((h) => rows.push({
    segment: 'check', name: h.name, kind: h.kind, when: h.applies_when, text: t(h.note), example: h.example,
  }));
  KNOBS.knobs.filter((k) => (k.group_key ? json.includes(`"key":"${k.group_key}"`) : !k.element || k.element.some(has)))
    .forEach((k) => rows.push({
      segment: 'buttons', name: k.name, kind: k.kind, when: k.applies_when, text: t(k.description), example: k.example,
    }));
  return rows;
}

/** The CSS "Kopieren" puts on the clipboard: example values with a comment on the kind (spec §9, Fable M5). */
export function knobsSnippet(role, rows, words) {
  const decl = rows.map((z) => `  ${z.name}: ${z.example};`);
  const width = Math.max(...decl.map((d) => d.length));
  const rowText = rows.map((z, i) => {
    const kind = z.kind === 'fixed' ? words.fixed : `${words.meaning}${z.when ? `: ${words.actsWhen} ${z.when}` : ''}`;
    return `${decl[i].padEnd(width)} /* ${kind} */`;
  });
  return `.wm-section[data-role="${role}"] {\n${rowText.join('\n')}\n}\n`;
}
